/**
 * High level router.
 *
 * PUBLIC routes  → render bare (each page handles its own layout internally)
 * AUTH routes    → wrapped in AuthLayout (Direction A sidebar + mobile nav)
 *
 * NLayout wraps everything as a pure context provider (UserContext, GA, toasts).
 */

import React, { useContext } from "react";
import { Switch, withRouter, Route, useLocation } from "react-router-dom";
import NLayout, { UserContext } from "../layout/NLayout";
import AuthGuard from "../components/AuthGuard/AuthGuard";
import Sidebar from "../components/Sidebar/Sidebar";
import MobileNav from "../components/MobileNav/MobileNav";
import GuestLayout from "../components/GuestLayout/GuestLayout";

// Direct imports — public pages
import Lifelanding from "../pages/Lifelanding/Home";
import CIlanding from "../pages/CIlanding/Home";
import DIlanding from "../pages/DIlanding/Home";
import Login from "../pages/Login/Login";
import Register from "../pages/Register/Register";
import Recovery from "../pages/Recovey/Recovery";
import Complete from "../components/CompleteQuote/CompleteQuote";
import TermOfService from "../pages/TermOfService/index";
import PrivacyPolicy from "../pages/PrivacyPolicy/index";

// Lazy imports
const Panel = React.lazy(() => import("../pages/Panel/Panel"));
const Qoute = React.lazy(() => import("../pages/Qoute/Qoute"));
const ForgotPassword = React.lazy(() =>
  import("../components/ForgotPassword/ForgotPassword")
);
const EmailVerify = React.lazy(() =>
  import("../components/EmailVerify/EmailVerify")
);
const Home = React.lazy(() => import('../pages/Home/Home'));
const HealthScore = React.lazy(() => import('../pages/HealthScore/HealthScore'));
const InvestorProfile = React.lazy(() => import('../pages/InvestorProfile/InvestorProfile'));

// Build 8.4.0 — New lazy-loaded pages
const Dashboard = React.lazy(() => import('../pages/Dashboard/Dashboard'));
const MyProgress = React.lazy(() => import('../pages/MyProgress/MyProgress'));
const Notifications = React.lazy(() => import('../pages/Notifications/Notifications'));
const WillChecklist = React.lazy(() => import('../pages/WillChecklist/WillChecklist'));
const MoneyMinutePage = React.lazy(() => import('../pages/MoneyMinutePage/MoneyMinutePage'));
const MyPolicies = React.lazy(() => import('../pages/MyPolicies/MyPolicies'));
const Markets = React.lazy(() => import('../pages/Markets/Markets'));

// Build 9 — New financial tools
const BalanceSheet = React.lazy(() => import('../pages/BalanceSheet/BalanceSheet'));
const CashFlow = React.lazy(() => import('../pages/CashFlow/CashFlow'));
const DebtPlanner = React.lazy(() => import('../pages/DebtPlanner/DebtPlanner'));
const RetirementPlanner = React.lazy(() => import('../pages/RetirementPlanner/RetirementPlanner'));
const InsuranceExplorer = React.lazy(() => import('../pages/InsuranceExplorer/InsuranceExplorer'));
const EstatePlanner = React.lazy(() => import('../pages/EstatePlanner/EstatePlanner'));
const EstateCostCalculator = React.lazy(() => import('../pages/EstateCostCalculator/EstateCostCalculator'));
const Book = React.lazy(() => import('../pages/Book/Book'));
const Tools = React.lazy(() => import('../pages/Tools/Tools'));

/* ───────────────────────────────────────────
   AuthLayout — Direction A sidebar + mobile nav
   Only used for authenticated routes.
   ─────────────────────────────────────────── */
function AuthLayout({ children }) {
  const location = useLocation();
  const { userData, userActions } = useContext(UserContext);

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#F0F4F9' }}>
      <div className="gc-sidebar-wrap" style={{ width: 240, flexShrink: 0 }}>
        <Sidebar
          quoteCount={0}
          notificationCount={0}
          onSignOut={userActions.logout}
          currentPath={location.pathname}
        />
      </div>
      <div style={{ flex: 1, minWidth: 0, padding: '24px', paddingBottom: 80 }}>
        {children}
      </div>
      <div className="gc-mobilenav-wrap">
        <MobileNav currentPath={location.pathname} />
      </div>
      <style>{`
        .gc-sidebar-wrap { display: block; }
        .gc-mobilenav-wrap { display: none; }
        @media (max-width: 768px) {
          .gc-sidebar-wrap { display: none; }
          .gc-mobilenav-wrap { display: block; }
        }
      `}</style>
    </div>
  );
}


export const Routes = withRouter(({ history }) => {
  return (
    <NLayout>
      <Switch>
        {/* ═══════════════════════════════════════════
            GUEST ACCESS ROUTES — Build 10.1
            Public tool previews — gated results via GuestGate
        ═══════════════════════════════════════════ */}
        <Route path="/try/health-score" exact>
          <GuestLayout><HealthScore guest={true} /></GuestLayout>
        </Route>
        <Route path="/try/estate-cost" exact>
          <GuestLayout><EstateCostCalculator guest={true} /></GuestLayout>
        </Route>
        <Route path="/try/cash-flow" exact>
          <GuestLayout><CashFlow guest={true} /></GuestLayout>
        </Route>
        <Route path="/try/retirement" exact>
          <GuestLayout><RetirementPlanner guest={true} /></GuestLayout>
        </Route>
        {/* === END GUEST ACCESS ROUTES === */}

        {/* ═══════════════════════════════════════════
            PUBLIC ROUTES — bare render, each page handles its own layout
            (Login uses LayoutEnter, Home uses its own, etc.)
            Identical to Build 7 master.
        ═══════════════════════════════════════════ */}
        <Route path="/" exact>
          <Home />
        </Route>
        <Route path="/life-insurance" exact>
          <Lifelanding />
        </Route>
        <Route path="/critical-insurance" exact>
          <CIlanding />
        </Route>
        <Route path="/Disability-insurance" exact>
          <DIlanding />
        </Route>
        <Route path="/health-score" exact>
          <AuthLayout><AuthGuard><HealthScore /></AuthGuard></AuthLayout>
        </Route>
        <Route path="/investor-profile" exact>
          <AuthLayout><AuthGuard><InvestorProfile /></AuthGuard></AuthLayout>
        </Route>
        <Route path="/forgot_password">
          <ForgotPassword />
        </Route>
        <Route path="/verify">
          <EmailVerify />
        </Route>
        <Route path="/email/verify/:id/:hash">
          <EmailVerify />
        </Route>
        <Route path="/login">
          <Login />
        </Route>
        <Route path="/register">
          <Register />
        </Route>
        <Route path="/recovery">
          <Recovery />
        </Route>
        <Route path="/Complete_quote">
          <Complete />
        </Route>
        <Route path="/Term-of-use">
          <TermOfService />
        </Route>
        <Route path="/Privacy-policy">
          <PrivacyPolicy />
        </Route>

        {/* ═══════════════════════════════════════════
            AUTHENTICATED ROUTES — AuthLayout (Sidebar + MobileNav)
        ═══════════════════════════════════════════ */}
        <Route path="/dashboard" exact>
          <AuthLayout><AuthGuard><Dashboard /></AuthGuard></AuthLayout>
        </Route>
        <Route path="/progress" exact>
          <AuthLayout><AuthGuard><MyProgress /></AuthGuard></AuthLayout>
        </Route>
        <Route path="/notifications" exact>
          <AuthLayout><AuthGuard><Notifications /></AuthGuard></AuthLayout>
        </Route>
        <Route path="/will-checklist" exact>
          <AuthLayout><AuthGuard><WillChecklist /></AuthGuard></AuthLayout>
        </Route>
        <Route path="/money-minute" exact>
          <AuthLayout><AuthGuard><MoneyMinutePage /></AuthGuard></AuthLayout>
        </Route>
        <Route path="/my-policies" exact>
          <AuthLayout><AuthGuard><MyPolicies /></AuthGuard></AuthLayout>
        </Route>
        <Route path="/markets" exact>
          <AuthLayout><AuthGuard><Markets /></AuthGuard></AuthLayout>
        </Route>
        <Route path="/balance-sheet" exact>
          <AuthLayout><AuthGuard><BalanceSheet /></AuthGuard></AuthLayout>
        </Route>
        <Route path="/cash-flow" exact>
          <AuthLayout><AuthGuard><CashFlow /></AuthGuard></AuthLayout>
        </Route>
        <Route path="/debt-planner" exact>
          <AuthLayout><AuthGuard><DebtPlanner /></AuthGuard></AuthLayout>
        </Route>
        <Route path="/retirement-planner" exact>
          <AuthLayout><AuthGuard><RetirementPlanner /></AuthGuard></AuthLayout>
        </Route>
        <Route path="/insurance-explorer" exact>
          <AuthLayout><AuthGuard><InsuranceExplorer /></AuthGuard></AuthLayout>
        </Route>
        <Route path="/estate-planner" exact>
          <AuthLayout><AuthGuard><EstatePlanner /></AuthGuard></AuthLayout>
        </Route>
        <Route path="/estate-cost" exact>
          <AuthLayout><AuthGuard><EstateCostCalculator /></AuthGuard></AuthLayout>
        </Route>
        <Route path="/tools" exact>
          <AuthLayout><AuthGuard><Tools /></AuthGuard></AuthLayout>
        </Route>
        <Route path="/book" exact>
          <AuthLayout><AuthGuard><Book /></AuthGuard></AuthLayout>
        </Route>
        <Route path="/panel">
          <AuthLayout><AuthGuard><Panel /></AuthGuard></AuthLayout>
        </Route>
        <Route path="/quote">
          <AuthGuard><Qoute /></AuthGuard>
        </Route>
      </Switch>
    </NLayout>
  );
});
